import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IKnmRegistryItem } from 'APIPackage/KnmModule/interfaces/IKnmRegistryItem';

import { knmSetLoadingFlags } from '../actions/knmActions';
import { KnmLoadingFlagsEnum } from '../enums/KnmLoadingFlagsEnum';

interface IUseRegistryFnsMatchData {
  knmRegistryData: IKnmRegistryItem[];
  isEgrul: boolean;
  isLoading: boolean;
}

export function useRegistryFnsMatchData({ knmRegistryData, isEgrul, isLoading }: IUseRegistryFnsMatchData) {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const setRegistryFnsMatchDataLoadingFlag = useCallback(
    (value: boolean) => dispatch(knmSetLoadingFlags(KnmLoadingFlagsEnum.REGISTRY_FNS_MATCH_DATA, value)),
    [dispatch],
  );

  const registryInfo = [...knmRegistryData].reverse();
  const [registryOrganizationNameFns, setRegistryOrganizationNameFns] = useState<string | undefined>();
  const [registryLegalAddressFns, setRegistryLegalAddressFns] = useState<string | undefined>();

  useEffect(() => {
    setRegistryFnsMatchDataLoadingFlag(isLoading);
  }, [isLoading, setRegistryFnsMatchDataLoadingFlag]);

  useEffect(() => {
    if (isEgrul) {
      setRegistryOrganizationNameFns(registryInfo.find(item => item.key === 'Наим ЮЛПолн')?.value);
    } else {
      const newRegistryOrganizationNameFnsParts: Array<string> = [];
      const organizationNameTitleIndex = registryInfo.findIndex(item => item.key === 'фиорус');
      const newRegistryFnsLastName =
        registryInfo.find((item, index) => index < organizationNameTitleIndex && item.key === 'фамилия')?.value || '';

      if (newRegistryFnsLastName) {
        newRegistryOrganizationNameFnsParts.push(newRegistryFnsLastName);
      }
      const newRegistryFnsFirstName =
        registryInfo.find((item, index) => index < organizationNameTitleIndex && item.key === 'имя')?.value || '';

      if (newRegistryFnsFirstName) {
        newRegistryOrganizationNameFnsParts.push(newRegistryFnsFirstName);
      }
      const newRegistryFnsMiddleName =
        registryInfo.find((item, index) => index < organizationNameTitleIndex && item.key === 'отчество')?.value || '';

      if (newRegistryFnsMiddleName) {
        newRegistryOrganizationNameFnsParts.push(newRegistryFnsMiddleName);
      }
      setRegistryOrganizationNameFns(newRegistryOrganizationNameFnsParts.join(' '));
    }
    const legalAddressTitleIndex = isEgrul
      ? registryInfo.findIndex(item => item.key === 'Св Адрес ЮЛ')
      : registryInfo.findIndex(item => item.key === 'Св Адр МЖ');
    const newRegistryLegalAddressFnsParts: Array<string> = [];
    const newRegistryLegalAddressFnsIndex =
      registryInfo.find((item, index) => index > legalAddressTitleIndex && item.key === 'индекс')?.value || '';

    if (newRegistryLegalAddressFnsIndex) {
      newRegistryLegalAddressFnsParts.push(newRegistryLegalAddressFnsIndex);
    }
    const newRegistryLegalAddressFnsRegionName =
      registryInfo.find((item, index) => index > legalAddressTitleIndex && item.key === 'Наим Регион')?.value || '';
    const newRegistryLegalAddressFnsRegionType =
      registryInfo.find((item, index) => index > legalAddressTitleIndex && item.key === 'Тип Регион')?.value || '';
    const newRegistryLegalAddressFnsRegion = `${
      newRegistryLegalAddressFnsRegionName ? `${newRegistryLegalAddressFnsRegionName} ` : ''
    }${newRegistryLegalAddressFnsRegionType}`;

    if (newRegistryLegalAddressFnsRegion) {
      newRegistryLegalAddressFnsParts.push(newRegistryLegalAddressFnsRegion);
    }
    const newRegistryLegalAddressFnsCityType =
      registryInfo.find((item, index) => index > legalAddressTitleIndex && item.key === 'Тип Город')?.value || '';
    const newRegistryLegalAddressFnsCityName =
      registryInfo.find((item, index) => index > legalAddressTitleIndex && item.key === 'Наим Город')?.value || '';
    const newRegistryLegalAddressFnsCity = `${
      newRegistryLegalAddressFnsCityType ? `${newRegistryLegalAddressFnsCityType} ` : ''
    }${newRegistryLegalAddressFnsCityName}`;

    if (newRegistryLegalAddressFnsCity) {
      newRegistryLegalAddressFnsParts.push(newRegistryLegalAddressFnsCity);
    }
    const newRegistryLegalAddressFnsStreetType =
      registryInfo.find((item, index) => index > legalAddressTitleIndex && item.key === 'Тип Улица')?.value || '';
    const newRegistryLegalAddressFnsStreetName =
      registryInfo.find((item, index) => index > legalAddressTitleIndex && item.key === 'Наим Улица')?.value || '';
    const newRegistryLegalAddressFnsStreet = `${
      newRegistryLegalAddressFnsStreetType ? `${newRegistryLegalAddressFnsStreetType} ` : ''
    }${newRegistryLegalAddressFnsStreetName}`;

    if (newRegistryLegalAddressFnsStreet) {
      newRegistryLegalAddressFnsParts.push(newRegistryLegalAddressFnsStreet);
    }
    const newRegistryLegalAddressFnsBuilding =
      registryInfo.find((item, index) => index > legalAddressTitleIndex && item.key === 'дом')?.value || '';

    if (newRegistryLegalAddressFnsBuilding) {
      newRegistryLegalAddressFnsParts.push(newRegistryLegalAddressFnsBuilding);
    }
    setRegistryLegalAddressFns(newRegistryLegalAddressFnsParts.join(', '));
  }, [isEgrul, registryInfo]);

  return { registryOrganizationNameFns, registryLegalAddressFns };
}
